import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Info } from 'lucide-react';
import { Button } from '@/components/Button';
import { Skeleton } from '@/components/Skeleton';
import { cn } from '@/lib/cn';
import { EtiquetaDeStatus } from '@/features/tickets/badges';
import { chaves } from '@/features/tickets/tickets.api';
import type { Chamado } from '@/features/tickets/types';
import { AvisoDeFalha, Chave } from '@/features/workflow/componentes';
import { DialogoDeTransicao, type DadosDaTransicao } from '@/features/workflow/DialogoDeTransicao';
import { classificarFalha, type Falha } from '@/features/workflow/recusa';
import { precisaDeFormulario } from '@/features/workflow/regras';
import type { OfertaDeTransicao, PassoAplicado } from '@/features/workflow/tipos';
import { chavesDeFluxo, executarTransicao, obterTransicoes } from '@/features/workflow/workflow.api';

/**
 * As transições do chamado, no lugar do seletor de situação.
 *
 * Quando há workflow vinculado, a situação **não se escolhe** — ela é
 * consequência do estado em que a transição deixa o chamado. Oferecer o
 * seletor antigo aqui seria oferecer um caminho que o servidor recusa com 409
 * apontando uma rota que a tela nem chamava.
 *
 * ## Por que a transição indisponível aparece apagada, e não sumida
 *
 * "Não vejo o botão" e "não posso usar o botão" ensinam coisas diferentes a
 * quem opera. O primeiro faz procurar a ação em outro lugar, perguntar ao
 * colega, abrir chamado interno. O segundo diz o que falta — *"defina o
 * responsável antes de pedir aprovação"* — e a pessoa resolve sozinha em dez
 * segundos.
 *
 * Por isso a tela pergunta antes de tentar: `GET .../transitions` devolve
 * `enabled` e `blockedBecause` para cada transição, calculados pelo mesmo
 * motor que executaria. A recusa no `POST` continua possível — o guard pode
 * mudar de resposta entre a leitura e o clique —, mas passa a ser exceção, e
 * não o caminho comum.
 */
export function PainelDeTransicoes({ chamado }: { chamado: Chamado }) {
  const cliente = useQueryClient();
  const [emAndamento, setEmAndamento] = useState<OfertaDeTransicao | null>(null);
  const [falha, setFalha] = useState<Falha | null>(null);
  const [aplicado, setAplicado] = useState<PassoAplicado[] | null>(null);

  const consulta = useQuery({
    queryKey: chavesDeFluxo.transicoes(chamado.id),
    queryFn: () => obterTransicoes(chamado.id),
  });

  const recarregar = () => {
    setFalha(null);
    void cliente.invalidateQueries({ queryKey: chaves.raiz });
  };

  const executar = useMutation({
    mutationFn: (entrada: { oferta: OfertaDeTransicao; dados: DadosDaTransicao }) =>
      executarTransicao(chamado.id, {
        key: entrada.oferta.key,
        ...(Object.keys(entrada.dados.fields).length > 0 ? { fields: entrada.dados.fields } : {}),
        ...(entrada.dados.reason ? { reason: entrada.dados.reason } : {}),
        // A `version` que esta tela leu. Quem a manda recebe 409 se outra
        // pessoa mexeu no chamado; quem não a manda escreve por cima sem saber.
        version: chamado.version,
      }),
    onMutate: () => {
      setFalha(null);
      setAplicado(null);
    },
    onSuccess: (resultado) => {
      setEmAndamento(null);
      setAplicado(resultado.applied);
      // Uma transição mexe na situação, no relógio de SLA, no rollup do pai e
      // na trilha. Invalidar a raiz de chamados é mais honesto do que enumerar
      // chaves e esquecer uma.
      void cliente.invalidateQueries({ queryKey: chaves.raiz });
    },
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  const acionar = (oferta: OfertaDeTransicao) => {
    setFalha(null);
    if (precisaDeFormulario(oferta)) {
      setEmAndamento(oferta);
      return;
    }
    executar.mutate({ oferta, dados: { fields: {} } });
  };

  if (consulta.isPending) {
    return (
      <div className="space-y-2" role="status" aria-busy aria-live="polite">
        <span className="sr-only">Carregando as ações do fluxo</span>
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-9 w-full" />
      </div>
    );
  }

  if (consulta.isError) {
    return <AvisoDeFalha falha={classificarFalha(consulta.error)} aoRecarregar={recarregar} />;
  }

  const { state, transitions, definitionKey } = consulta.data;

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <p className="text-sm font-medium">Situação</p>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold">{state.name}</span>
          <EtiquetaDeStatus status={state.category} />
          {state.terminal ? (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
              estado final
            </span>
          ) : null}
        </div>
        <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          Fluxo <Chave valor={definitionKey} /> · estado <Chave valor={state.key} />
        </p>
      </div>

      {transitions.length === 0 ? (
        <p className="flex items-start gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 flex-none" aria-hidden />
          <span>
            {state.terminal
              ? 'O chamado chegou ao fim do fluxo: deste estado não sai mais nenhuma transição.'
              : 'Nenhuma transição manual sai deste estado. O que move o chamado daqui é um evento — uma aprovação decidida, por exemplo — ou uma transição automática.'}
          </span>
        </p>
      ) : (
        <ul className="space-y-2">
          {transitions.map((oferta) => (
            <BotaoDeTransicao
              key={oferta.key}
              oferta={oferta}
              ocupado={executar.isPending}
              aoAcionar={() => acionar(oferta)}
            />
          ))}
        </ul>
      )}

      {aplicado && aplicado.length > 0 ? <ResumoDoQueAconteceu passos={aplicado} /> : null}

      {/* A recusa do diálogo é mostrada lá dentro, junto com o que foi
          digitado; aqui fica a das transições que não abrem formulário. */}
      {falha && emAndamento === null ? (
        <AvisoDeFalha falha={falha} aoRecarregar={recarregar} />
      ) : null}

      {emAndamento ? (
        <DialogoDeTransicao
          oferta={emAndamento}
          aberto
          ocupado={executar.isPending}
          falha={falha}
          aoRecarregar={() => {
            // O 409 do diálogo é o mesmo do painel: a versão que a tela leu
            // envelheceu. Sem passar isto, o aviso prometia "Recarregar traz o
            // estado de agora" e não mostrava botão nenhum — e cada nova
            // tentativa reenviava a mesma versão.
            setEmAndamento(null);
            recarregar();
          }}
          aoFechar={() => {
            setEmAndamento(null);
            setFalha(null);
          }}
          aoConfirmar={(dados) => executar.mutate({ oferta: emAndamento, dados })}
        />
      ) : null}
    </div>
  );
}

/**
 * Um botão por transição — e o motivo embaixo, quando ele está apagado.
 *
 * O motivo é texto visível e não `title`: botão desabilitado não recebe foco,
 * então nem o teclado nem o leitor de tela chegariam a um tooltip. `aria-
 * describedby` amarra os dois para quem navega pela árvore de acessibilidade.
 */
function BotaoDeTransicao({
  oferta,
  ocupado,
  aoAcionar,
}: {
  oferta: OfertaDeTransicao;
  ocupado: boolean;
  aoAcionar: () => void;
}) {
  const idDoMotivo = `motivo-${oferta.key}`;
  // O que a ação vai pedir antes de acontecer, dito no próprio botão: abrir um
  // formulário sem aviso é o tipo de surpresa que faz a pessoa cancelar por
  // engano no meio de um atendimento.
  const exige = precisaDeFormulario(oferta)
    ? [
        ...(oferta.requiredFields.length > 0
          ? [`${oferta.requiredFields.length} campo(s)`]
          : []),
        ...(oferta.requiresReason ? ['justificativa'] : []),
      ].join(' e ')
    : null;

  return (
    <li className="space-y-1">
      <Button
        variant={oferta.enabled ? 'secondary' : 'ghost'}
        size="sm"
        className={cn('w-full justify-between', !oferta.enabled && 'border border-dashed border-border')}
        disabled={!oferta.enabled || ocupado}
        aria-describedby={oferta.blockedBecause ? idDoMotivo : undefined}
        onClick={aoAcionar}
      >
        <span className="truncate">{oferta.name}</span>
        {exige ? (
          <span className="ml-2 flex-none text-xs font-normal text-muted-foreground">
            pede {exige}
          </span>
        ) : null}
      </Button>

      {oferta.blockedBecause ? (
        <p
          id={idDoMotivo}
          className="rounded-md border border-warning/25 bg-warning/5 px-2.5 py-1.5 text-xs text-warning"
        >
          {oferta.blockedBecause}
        </p>
      ) : null}
    </li>
  );
}

/**
 * O que a transição causou, incluindo as automáticas.
 *
 * Uma transição pode destravar outras `AUTO` — a revisão pós-incidente que se
 * abre sozinha, por exemplo. Sem este resumo, o chamado apareceria dois
 * estados adiante do botão que a pessoa apertou, sem nada explicando o salto.
 */
function ResumoDoQueAconteceu({ passos }: { passos: readonly PassoAplicado[] }) {
  return (
    <div className="rounded-md border border-success/25 bg-success/5 px-3 py-2 text-xs text-success">
      <p className="font-semibold">
        {passos.length === 1 ? 'Transição aplicada' : `${passos.length} transições aplicadas`}
      </p>
      <ul className="mt-1 space-y-0.5">
        {passos.map((passo) => (
          <li key={passo.transitionLogId}>
            {passo.fromState} → {passo.toState}
            {passo.automatic ? ' (automática)' : ''}
          </li>
        ))}
      </ul>
    </div>
  );
}
